import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import { stErrorMessage, stAuditLogs, StAuditLog } from '../../../services/socialTradingService';
import { StIdCell, StScrollTable, StStatusChip, stFormatDateTime, stText } from '../StListUi';

const ACTIONS = [
  '',
  'USER_BAN',
  'USER_UNBAN',
  'ROLE_PERMISSION_UPDATE',
  'PRIVATE_CHAT_AUTH_UPDATE',
  'ROOM_UPDATE',
  'ROOM_MUTE',
  'INVITE_CODE_CREATE',
  'AGENT_ROOM_BIND',
  'SIGNAL_PARSE_FAILURE',
  'REDPACKET_SEND',
  'REDPACKET_WITHDRAWAL_FAILURE',
  'CLAIM_THRESHOLD_UPDATE',
  'REDPACKET_BALANCE_RECLAIM',
  'POOL_CONFIGURE',
  'POOL_DISABLE',
];

const RESULTS = ['', 'SUCCESS', 'FAILURE'];
const SOURCES = ['', 'IDENTITY', 'ROOM', 'AGENT', 'CHAT', 'REDPACKET', 'POOL', 'SYSTEM_CONFIG'];

const ACTION_KEYS: Record<string, string> = {
  USER_BAN: 'module.socialTrading.actionUSER_BAN',
  USER_UNBAN: 'module.socialTrading.actionUSER_UNBAN',
  ROLE_PERMISSION_UPDATE: 'module.socialTrading.actionROLE_PERMISSION_UPDATE',
  PRIVATE_CHAT_AUTH_UPDATE: 'module.socialTrading.actionPRIVATE_CHAT_AUTH_UPDATE',
  ROOM_UPDATE: 'module.socialTrading.actionROOM_UPDATE',
  ROOM_MUTE: 'module.socialTrading.actionROOM_MUTE',
  INVITE_CODE_CREATE: 'module.socialTrading.actionINVITE_CODE_CREATE',
  AGENT_ROOM_BIND: 'module.socialTrading.actionAGENT_ROOM_BIND',
  SIGNAL_PARSE_FAILURE: 'module.socialTrading.actionSIGNAL_PARSE_FAILURE',
  REDPACKET_SEND: 'module.socialTrading.actionREDPACKET_SEND',
  REDPACKET_WITHDRAWAL_FAILURE: 'module.socialTrading.actionREDPACKET_WITHDRAWAL_FAILURE',
  CLAIM_THRESHOLD_UPDATE: 'module.socialTrading.actionCLAIM_THRESHOLD_UPDATE',
  REDPACKET_BALANCE_RECLAIM: 'module.socialTrading.actionREDPACKET_BALANCE_RECLAIM',
  POOL_CONFIGURE: 'module.socialTrading.actionPOOL_CONFIGURE',
  POOL_DISABLE: 'module.socialTrading.actionPOOL_DISABLE',
};

const SOURCE_LABEL: Record<string, string> = {
  IDENTITY: 'IDENTITY',
  ROOM: 'ROOM',
  AGENT: 'AGENT',
  CHAT: 'CHAT',
  REDPACKET: 'REDPACKET',
  POOL: 'POOL',
  SYSTEM_CONFIG: 'SYSTEM_CONFIG',
};

const RESULT_KEYS: Record<string, string> = {
  SUCCESS: 'module.socialTrading.resultSUCCESS',
  FAILURE: 'module.socialTrading.resultFAILURE',
};

const PAGE_SIZE = 20;

function formatObject(row: StAuditLog): { type: string; id?: string } {
  try {
    const o = JSON.parse(row.object || '{}') as Record<string, unknown>;
    const id =
      o.userId ||
      o.withdrawalId ||
      o.messageRef ||
      o.signalRef ||
      o.roomId ||
      o.key;
    if (id) return { type: row.objectType || '', id: String(id) };
  } catch {
    /* ignore */
  }
  return { type: row.objectType || row.object || '—' };
}

function shortJson(v?: string | null): string {
  if (!v) return '—';
  return v.length > 80 ? `${v.slice(0, 80)}…` : v;
}

export default function SocialTradingAuditList() {
  const t = useTranslate();
  const notify = useNotify();
  const [items, setItems] = useState<StAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [action, setAction] = useState('');
  const [result, setResult] = useState('');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await stAuditLogs({
        keyword: keyword || undefined,
        action: action || undefined,
        result: result || undefined,
        source: source || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [keyword, action, result, source, from, to, page, notify]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PermissionGuard resource="social-trading-audit" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.audit')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.audit') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          {t('module.socialTrading.auditHint')}
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <TextField
                size="small"
                label={t('module.socialTrading.filterKeyword')}
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
              />
              <TextField
                select
                size="small"
                label={t('module.socialTrading.filterAction')}
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">{t('module.socialTrading.filterAll')}</MenuItem>
                {ACTIONS.filter(Boolean).map((a) => (
                  <MenuItem key={a} value={a}>
                    {ACTION_KEYS[a] ? t(ACTION_KEYS[a]) : a}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={t('module.socialTrading.filterResult')}
                value={result}
                onChange={(e) => {
                  setResult(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">{t('module.socialTrading.filterAll')}</MenuItem>
                {RESULTS.filter(Boolean).map((r) => (
                  <MenuItem key={r} value={r}>
                    {RESULT_KEYS[r] ? t(RESULT_KEYS[r]) : r}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={t('module.socialTrading.filterSource')}
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">{t('module.socialTrading.filterAll')}</MenuItem>
                {SOURCES.filter(Boolean).map((s) => (
                  <MenuItem key={s} value={s}>
                    {SOURCE_LABEL[s] || s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="datetime-local"
                label={t('module.socialTrading.filterFrom')}
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(0);
                }}
              />
              <TextField
                size="small"
                type="datetime-local"
                label={t('module.socialTrading.filterTo')}
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(0);
                }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  if (page !== 0) setPage(0);
                  else load();
                }}
              >
                {t('module.socialTrading.search')}
              </Button>
              <Typography sx={{ alignSelf: 'center' }}>
                {t('module.socialTrading.auditTotal', { smart_count: total })}
              </Typography>
            </Stack>

            <StScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.socialTrading.colId')}</TableCell>
                  <TableCell>{t('module.socialTrading.colType')}</TableCell>
                  <TableCell>{t('module.socialTrading.colOperator')}</TableCell>
                  <TableCell>{t('module.socialTrading.colObject')}</TableCell>
                  <TableCell>{t('module.socialTrading.colRoom')}</TableCell>
                  <TableCell>{t('module.socialTrading.colFromTo')}</TableCell>
                  <TableCell>{t('module.socialTrading.colReason')}</TableCell>
                  <TableCell>{t('module.socialTrading.colResult')}</TableCell>
                  <TableCell>{t('module.socialTrading.colSource')}</TableCell>
                  <TableCell>{t('module.socialTrading.colTime')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => {
                  const obj = formatObject(row);
                  return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <StIdCell value={row.id} />
                    </TableCell>
                    <TableCell>{ACTION_KEYS[row.action] ? t(ACTION_KEYS[row.action]) : row.action}</TableCell>
                    <TableCell>{stText(row.operator)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" component="span">
                          {obj.type || '—'}
                        </Typography>
                        {obj.id ? <StIdCell value={obj.id} /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{stText(row.roomName)}</TableCell>
                    <TableCell>
                      <Tooltip title={`${row.fromValue || '—'} → ${row.toValue || '—'}`}>
                        <Typography variant="body2" component="span" noWrap sx={{ maxWidth: 220, display: 'inline-block' }}>
                          {shortJson(row.fromValue)} → {shortJson(row.toValue)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{stText(row.reason)}</TableCell>
                    <TableCell>
                      <StStatusChip status={row.result} />
                    </TableCell>
                    <TableCell>{SOURCE_LABEL[row.source] || row.source}</TableCell>
                    <TableCell>{stText(stFormatDateTime(row.createdAt))}</TableCell>
                  </TableRow>
                  );
                })}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography variant="body2" color="text.secondary">
                        {t('module.socialTrading.emptyAudit')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StScrollTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </CardContent>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
